const initialState = {
  messageList: [],
  agentsList: [],
  currentStreamingDataChunk: { role: "assistant", content: "", params: {} },
  intermediateSteps: [],
  sessionId: null,
  suggestionQueries: [],
  isLoadingRes: false,
  isChunkEnded: false,
  isAuthenticated: false,
  errorMsg: "",
  forceStopStreamingRes: false,
  activeRequestId: null, // Track ongoing stream to ignore stale chunks
  lastEventTs: 0,
  version: 1,
};

// Action types for cross-tab sync and reducer
export const AI_BOT_ACTION_TYPES = {
  HYDRATE: "AI_BOT_HYDRATE",
  AUTH_UPDATED: "AI_BOT_AUTH_UPDATED",
  SESSION_UPDATED: "AI_BOT_SESSION_UPDATED",
  USER_MESSAGE_ACCEPTED: "AI_BOT_USER_MESSAGE_ACCEPTED",
  STREAM_STARTED: "AI_BOT_STREAM_STARTED",
  STREAM_CHUNK: "AI_BOT_STREAM_CHUNK",
  INTERMEDIATE_STEP: "AI_BOT_INTERMEDIATE_STEP",
  STREAM_END: "AI_BOT_STREAM_END",
  STOPPED: "AI_BOT_STOPPED",
  ERROR_UPDATED: "AI_BOT_ERROR_UPDATED",
  RESET: "AI_BOT_RESET",
  // Legacy actions for backward compat (from reducer.js)
  SET_AI_SUPPORTBOT: "SET_AI_SUPPORTBOT",
  SET_CHATBOT_MESSAGES: "SET_CHATBOT_MESSAGES",
  ADD_CHATBOT_MESSAGE: "ADD_CHATBOT_MESSAGE",
  SET_CHATBOT_AGENTS: "SET_CHATBOT_AGENTS",
  AUTH_SYNC: "AI_BOT_AUTH_SYNC",
  UPDATE_MESSAGE_FEEDBACK: "AI_BOT_UPDATE_MESSAGE_FEEDBACK",
};

const aiSupportBot = (state = initialState, action) => {
  switch (action.type) {
    case AI_BOT_ACTION_TYPES.HYDRATE: {
      // Hydrate from snapshot or backend; preserve auth-sensitive fields
      const {
        messageList,
        currentStreamingDataChunk,
        intermediateSteps,
        sessionId,
        suggestionQueries,
        isLoadingRes,
        isChunkEnded,
        errorMsg,
        version,
      } = action.payload;
      return {
        ...state,
        messageList: messageList ?? state.messageList,
        currentStreamingDataChunk:
          currentStreamingDataChunk ?? state.currentStreamingDataChunk,
        intermediateSteps: intermediateSteps ?? state.intermediateSteps,
        sessionId: sessionId ?? state.sessionId,
        suggestionQueries: suggestionQueries ?? state.suggestionQueries,
        isLoadingRes: isLoadingRes ?? state.isLoadingRes,
        isChunkEnded: isChunkEnded ?? state.isChunkEnded,
        errorMsg: errorMsg ?? state.errorMsg,
        version: version ?? state.version,
        lastEventTs: action.payload.ts || Date.now(),
      };
    }

    case AI_BOT_ACTION_TYPES.AUTH_UPDATED: {
      const { isAuthenticated, sessionId, errorMsg, suggestionQueries } =
        action.payload;
      return {
        ...state,
        isAuthenticated,
        sessionId: sessionId ?? state.sessionId,
        errorMsg: errorMsg ?? "",
        suggestionQueries: suggestionQueries ?? state.suggestionQueries,
        lastEventTs: action.payload.ts || Date.now(),
      };
    }

    case AI_BOT_ACTION_TYPES.SESSION_UPDATED: {
      return {
        ...state,
        sessionId: action.payload.sessionId,
        lastEventTs: action.payload.ts || Date.now(),
      };
    }

    case AI_BOT_ACTION_TYPES.USER_MESSAGE_ACCEPTED: {
      const { message, sessionId: sid } = action.payload;
      return {
        ...state,
        messageList: [message, ...state.messageList],
        sessionId: sid ?? state.sessionId,
        lastEventTs: action.payload.ts || Date.now(),
      };
    }

    case AI_BOT_ACTION_TYPES.STREAM_STARTED: {
      return {
        ...state,
        isLoadingRes: true,
        isChunkEnded: false,
        forceStopStreamingRes: false,
        currentStreamingDataChunk: {
          role: "assistant",
          content: "",
          params: {},
        },
        intermediateSteps: [],
        activeRequestId: action.payload.requestId,
        lastEventTs: action.payload.ts || Date.now(),
      };
    }

    case AI_BOT_ACTION_TYPES.STREAM_CHUNK: {
      const { requestId, content, params } = action.payload;
      // Ignore chunk if it belongs to a different request
      if (requestId !== state.activeRequestId) {
        return state;
      }
      return {
        ...state,
        currentStreamingDataChunk: {
          ...state.currentStreamingDataChunk,
          content:
            (state.currentStreamingDataChunk.content || "") + (content || ""),
          params: { ...state.currentStreamingDataChunk.params, ...params },
        },
        lastEventTs: action.payload.ts || Date.now(),
      };
    }

    case AI_BOT_ACTION_TYPES.INTERMEDIATE_STEP: {
      return {
        ...state,
        intermediateSteps: [...state.intermediateSteps, action.payload.step],
        lastEventTs: action.payload.ts || Date.now(),
      };
    }

    case AI_BOT_ACTION_TYPES.STREAM_END: {
      const { requestId } = action.payload;
      // Ignore if not the active request
      if (requestId !== state.activeRequestId) {
        return state;
      }
      return {
        ...state,
        isChunkEnded: true,
        lastEventTs: action.payload.ts || Date.now(),
      };
    }

    case AI_BOT_ACTION_TYPES.STOPPED: {
      return {
        ...state,
        isLoadingRes: false,
        forceStopStreamingRes: true,
        activeRequestId: null,
        lastEventTs: action.payload.ts || Date.now(),
      };
    }

    case AI_BOT_ACTION_TYPES.ERROR_UPDATED: {
      return {
        ...state,
        errorMsg: action.payload.errorMsg,
        isLoadingRes: false,
        lastEventTs: action.payload.ts || Date.now(),
      };
    }

    case AI_BOT_ACTION_TYPES.RESET: {
      return {
        ...initialState,
        // sessionId is cleared along with all other state
      };
    }

    case AI_BOT_ACTION_TYPES.SET_CHATBOT_AGENTS:
      return { ...state, agentsList: action.payload };

    // Legacy support
    case AI_BOT_ACTION_TYPES.SET_AI_SUPPORTBOT:
      return {
        ...state,
        ...action.payload,
      };

    // Support for reducer.js actions (chatbot reducer)
    case AI_BOT_ACTION_TYPES.SET_CHATBOT_MESSAGES:
      return {
        ...state,
        messageList: action.payload,
        lastEventTs: Date.now(),
      };

    case AI_BOT_ACTION_TYPES.ADD_CHATBOT_MESSAGE:
      return {
        ...state,
        messageList: [action.payload, ...state.messageList],
        lastEventTs: Date.now(),
      };

    case AI_BOT_ACTION_TYPES.UPDATE_MESSAGE_FEEDBACK: {
      const { messageId, liked } = action.payload;
      return {
        ...state,
        messageList: state.messageList.map((msg) =>
          msg.messageId === messageId ? { ...msg, liked } : msg
        ),
        lastEventTs: Date.now(),
      };
    }

    default:
      return state;
  }
};

// Action creators from reducer.js for backward compatibility
export const setChatbotMessages = (messages) => ({
  type: AI_BOT_ACTION_TYPES.SET_CHATBOT_MESSAGES,
  payload: messages,
});

export const addChatbotMessage = (message) => ({
  type: AI_BOT_ACTION_TYPES.ADD_CHATBOT_MESSAGE,
  payload: message,
});

export const setChatbotAgents = (agents) => ({
  type: AI_BOT_ACTION_TYPES.SET_CHATBOT_AGENTS,
  payload: agents,
});

// Action creators for auth and session management
export const updateAuth = (payload) => ({
  type: AI_BOT_ACTION_TYPES.AUTH_UPDATED,
  payload: {
    ...payload,
    ts: Date.now(),
  },
});

export const updateSession = (sessionId) => ({
  type: AI_BOT_ACTION_TYPES.SESSION_UPDATED,
  payload: {
    sessionId,
    ts: Date.now(),
  },
});

export const updateMessageFeedback = (messageId, liked) => ({
  type: AI_BOT_ACTION_TYPES.UPDATE_MESSAGE_FEEDBACK,
  payload: { messageId, liked },
});

export default aiSupportBot;
